// ---------------------------------------------------------------------------
// The search bot — sample the unknown, simulate each plan, keep the best
// ---------------------------------------------------------------------------
//
// searchBot(view, config, rng, { samples, candidates, weights, profile, log })
//   → orders (a plan submitOrders accepts)
//
// Each round, from the player's view alone (bots/view.js):
//   1. N opponent Grids consistent with everything the player knows
//      (bots/sampler.js)
//   2. K candidate plans, each legal by validateOrders (bots/candidates.js),
//      targets drawn from how often the samples put a structure in a cell
//   3. for each sample, the opponent's orders from a fast model, with their
//      yield taken as 0 (a player never sees it, so the model buys nothing):
//        'random'  the random legal bot on that sample (the fast preset)
//        'search'  the fast search bot deciding from the opponent's seat — a
//                  1-ply greedy opponent
//        'mixed'   half the samples each (the strong preset): hedged, so it
//                  neither over-defends against a weak opponent nor
//                  under-defends against a good one
//      The same opponent orders face every candidate (common random numbers:
//      the candidates are compared on identical futures)
//   4. every candidate × sample through submitOrders + resolveRound, scored
//      by evaluate() from this seat; a candidate's score is its mean
//   5. the best mean wins
//
// Look-ahead (`depth`, off): after this round, both sides play `depth` more
// rounds with rollouts.rolloutPolicy, and a candidate's score is `blend` of
// where that leaves the war plus the rest of this round's score. Measured
// against the scripted brooder (400 wars each) it does not pay yet: the fast
// bot fell from 95.5% to 72% / 66% / 64% at depth 1 / 2 / 3 (blend 1), 80% at
// depth 2 with blend 0.5; head to head at equal samples (6) it won 46-47%
// against the same bot without it. A look-ahead is only as good as the play it
// imagines, and the rollout policy's isn't good enough to beat the
// evaluation's own sense of the future (brood eggs, built-up edges).
//
// Threats (`threatAware`, `worstCase`; bots/threats.js): threat-aware, the
// candidates deploy against what the opponent can LEGALLY strike with in each
// corridor this round (nothing where nothing started the round or an airdrop
// just landed; neighbours' movers counted), weighted by what a crater won
// there could reach of ours, and add the guard and safe-brood plans.
// `worstCase` adds that many futures where the opponent strikes as hard as it
// legally can from the next most dangerous corridor, so a strike the model
// calls rare is still priced. `planWorstCase` adds that many per plan, ranked
// against the plan's own board, so one that lays a brood beside an open
// corridor is judged as if that corridor strikes — as one future among the
// rest, or (`planWorstWeight` p) as a fixed share p of its score.
//
// Presets: PRESETS.fast (3 samples, 8 candidates, random opponent model) for
// bulk runs, PRESETS.strong (10 samples, 24 candidates, mixed opponent
// model) for analysis. `log`, when given, receives
// { round, chosen, style, best, second, ms, candidates, samples }.

import { withRules } from '../config.js'
import { submitOrders } from '../submitOrders.js'
import { resolveRound } from '../resolveRound.js'
import { randomOrders } from './randomBot.js'
import { sampleOpponentGrid, structureOdds } from './sampler.js'
import { generateCandidates, threatTable } from './candidates.js'
import { evaluate, DEFAULT_WEIGHTS } from './evaluate.js'
import { viewOfOpponentGrid } from './view.js'
import { rolloutPolicy } from './rollout.js'
import { playerPolicy } from './playerBot.js'
import { seededRng } from './rng.js'
import { pressureOrders } from './threats.js'
import { learnedEvaluate } from './valueNet.js'

export const PRESETS = Object.freeze({
    fast: Object.freeze({ samples: 3, candidates: 8, opponentModel: 'random' }),
    strong: Object.freeze({ samples: 10, candidates: 24, opponentModel: 'mixed' }),
})

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

export function searchBot(view, config, rng, opts = {}) {
    return withRules(config, () => decide(view, rng, opts))
}

function decide(view, rng, { samples = PRESETS.fast.samples, candidates = PRESETS.fast.candidates,
    weights = DEFAULT_WEIGHTS, profile = null, log = null, ignoreIntel = false, opponentModel = 'random', caution = 0, depth = 0, blend = 0.5, rolloutOpp = 'policy', rolloutSelf = 'policy',
    threatAware = false, worstCase = 0, planWorstCase = 0, planWorstWeight = null, coverBroods = true, valueModel = null, gapClose = true, playerModel = undefined } = {}) {
    const t0 = now()
    // Intel ignored (the report's control): the stamps are dropped from what
    // the bot reasons with, so its samples ignore every bearing it holds.
    if (ignoreIntel) view = { ...view, opp: { ...view.opp, grid: { ...view.opp.grid, stamps: [] } } }
    const seat = view.seat
    const other = seat === 'p1' ? 'p2' : 'p1'
    const hives = []
    for (let i = 0; i < samples; i++) hives.push(sampleOpponentGrid(view.opp.grid, view.opp.structuresLost, rng))
    const odds = structureOdds(hives)
    const plans = generateCandidates(view, rng, candidates, odds, profile, { threatAware, coverBroods, gapClose })
    const w0 = profile?.weights ? { ...weights, ...profile.weights } : weights
    const context = w0.threat ? { threat: threatTable(view, odds) } : null
    const me = { board: view.self.board, homefront: view.self.homefront, yield: view.self.yield, structuresLost: view.self.structuresLost }

    // The opponent, once per sample: the random bot, or ('search') the fast
    // search bot deciding from the opponent's seat — its view of OUR Grid is
    // exactly what the fog shows it, its own Grid is the sample.
    const futures = hives.map((hf, i) => {
        const opp = { board: view.opp.board, homefront: hf, yield: 0, structuresLost: view.opp.structuresLost }
        let orders
        if (opponentModel === 'player') {
            // As the fitted player model plays (rules/bots/playerBot.js), from
            // what they can see of us.
            orders = playerPolicy(opp, view.self.board, viewOfOpponentGrid(view.self.homefront), rng, playerModel)
        } else if (opponentModel === 'search' || (opponentModel === 'mixed' && i % 2 === 1)) {
            const theirView = {
                seat: other, round: view.round, capacities: view.capacities,
                self: opp,
                opp: { board: view.self.board, structuresLost: view.self.structuresLost, grid: viewOfOpponentGrid(view.self.homefront) },
            }
            orders = decide(theirView, rng, { ...PRESETS.fast, weights })
        } else {
            orders = randomOrders(opp, rng)
        }
        try { return submitOrders(opp, orders) } catch { return null }
    }).filter(Boolean)
    // Worst cases: `worstCase` more futures, each the opponent striking as
    // hard as it legally can from the next most dangerous corridor
    // (threats.pressureOrders) — so a strike the model calls rare is still
    // priced, not left out of every future.
    const ours = { board: view.self.board, homefront: view.self.homefront, structuresLost: view.self.structuresLost }
    for (let i = 0; i < worstCase && hives.length; i++) {
        const opp = { board: view.opp.board, homefront: hives[i % hives.length], yield: 0, structuresLost: view.opp.structuresLost }
        const orders = pressureOrders(opp, ours, i)
        if (!orders) break
        try { futures.push(submitOrders(opp, orders)) } catch { /* skip */ }
    }

    const w = profile?.weights ? { ...weights, ...profile.weights } : weights
    // The position's worth: the hand-written evaluation, or (valueModel) the
    // learned win probability (bots/valueNet.js).
    const score = valueModel ? (r) => learnedEvaluate(r, seat, view.round, valueModel) : (r) => evaluate(r, seat, w, context)
    // Look-ahead: each future gets its own rollout seed, shared by every
    // candidate, so they are compared on the same imagined continuations.
    const rolloutSeeds = futures.map(() => Math.floor(rng() * 2 ** 31))
    const playOn = (r, j) => {
        let last = r
        let round = view.round
        const prng = seededRng(rolloutSeeds[j])
        for (let d = 0; d < depth && !last.outcome.over; d++) {
            round++
            const next = {}
            for (const k of [seat, other]) {
                const them = k === seat ? other : seat
                // Our side aims with what we believe (the odds), not the sample's
                // truth; theirs plays rolloutPolicy, or ('player') as the fitted
                // player model does, from what they can see of us.
                const asPlayer = k === seat ? rolloutSelf === 'player' : rolloutOpp === 'player'
                const orders = asPlayer
                    ? playerPolicy(last[k], last[them].board, viewOfOpponentGrid(last[them].homefront), prng)
                    : rolloutPolicy(last[k], last[them], prng, k === seat ? odds : null)
                try { next[k] = submitOrders(last[k], orders) }
                catch { next[k] = null }
            }
            if (!next[seat] || !next[other]) break
            last = resolveRound({ [seat]: next[seat], [other]: next[other], round })
        }
        return last
    }
    const scored = plans.map(plan => {
        const mine = submitOrders(me, plan)
        const vals = []
        const worst = []
        // Per-plan worst cases: the opponent striking as hard as it legally
        // can at THIS plan's weakest corridors — its deploys, moves and the
        // broods it lays included — so a plan that leaves a new brood beside
        // an open corridor pays for it, and one that covers it doesn't.
        for (let i = 0; i < planWorstCase && hives.length; i++) {
            const opp = { board: view.opp.board, homefront: hives[i % hives.length], yield: 0, structuresLost: view.opp.structuresLost }
            const orders = pressureOrders(opp, { board: plan.board, homefront: view.self.homefront, structuresLost: view.self.structuresLost }, i)
            if (!orders) break
            let theirs
            try { theirs = submitOrders(opp, orders) } catch { continue }
            const r = resolveRound({ [seat]: mine, [other]: theirs, round: view.round })
            const v = score(r) + (profile?.bonus ? profile.bonus(r, seat, plan, view) : 0)
            if (planWorstWeight == null) vals.push(v); else worst.push(v)
        }
        futures.forEach((theirs, j) => {
            const r = resolveRound({ [seat]: mine, [other]: theirs, round: view.round })
            const now = score(r)
            // With look-ahead: `blend` of the score where the rollout ends, the
            // rest of this round's.
            const later = depth ? score(playOn(r, j)) : now
            vals.push((1 - blend) * now + blend * later + (profile?.bonus ? profile.bonus(r, seat, plan, view) : 0))
        })
        // Cautious: the mean, less `caution` times the spread — a plan that
        // pays only in a few lucky samples loses to a steady one.
        let mean = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
        // With `planWorstWeight`, the per-plan worst cases are a fixed share
        // of the score rather than one future among many.
        if (worst.length) mean = (1 - planWorstWeight) * mean + planWorstWeight * worst.reduce((a, b) => a + b, 0) / worst.length
        const sd = vals.length ? Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length) : 0
        return { plan, score: mean - caution * sd }
    }).sort((a, b) => b.score - a.score)

    const best = scored[0]
    if (log) {
        log({
            round: view.round, style: best?.plan.style ?? null,
            best: best?.score ?? null, second: scored[1]?.score ?? null,
            ms: now() - t0, candidates: plans.length, samples: futures.length,
            chosen: best ? summarize(best.plan) : null,
        })
    }
    if (!best) throw new Error('searchBot: no legal candidate')
    const { style, broodCols, ...orders } = best.plan
    void style; void broodCols
    return orders
}

// A plan in a line of plain facts, for the war log.
export function summarize(plan) {
    const deploys = []
    plan.board.forEach((bf, c) => { for (let i = 0; i < (bf.newArmies || 0) - (bf.deliveredNewArmies || 0); i++) deploys.push(c + 1) })
    return {
        deploys,
        moves: plan.board.map((bf, c) => (bf.movedOut ? c + 1 : null)).filter(Boolean),
        broods: plan.board.flatMap((bf, c) => (bf.pendingBaseBuilds ?? []).map(() => c + 1)),
        attacks: plan.board.map((bf, c) => (bf.isAttacking ? c + 1 : null)).filter(Boolean),
        rockets: plan.rockets.length,
        planes: plan.drones.length,
        dropZones: plan.dropZones.map(c => c + 1),
    }
}
